import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSidebar } from "../SidebarContext";
import { useTheme } from "../ThemeContext";
/* eslint-disable-next-line no-unused-vars -- motion.span used in JSX (member expression not tracked by no-unused-vars) */
import { motion, AnimatePresence } from "framer-motion";

const CHAR_H = 29;
const LH = 1.4;
const LINE_H = CHAR_H * LH;

function LetterPullUp({ text, isHovered }) {
  return (
    <span className="inline-flex gap-px">
      {text.split("").map((char, i) => (
        <span
          key={i}
          style={{ display: "inline-block", overflow: "hidden", height: `${LINE_H}px`, verticalAlign: "bottom" }}
        >
         <motion.span
  style={{ display: "block", height: `${LINE_H}px`, lineHeight: `${LINE_H}px`, whiteSpace: "pre" }}
  animate={{ y: isHovered ? -LINE_H : 0 }}
  transition={{
    type: "spring",
    stiffness: 120,
    damping: 20,
    mass: 0.8,
    delay: isHovered ? i * 0.01 : (text.length - 1 - i) * 0.01,
  }}
>
            <span style={{ display: "block", height: `${LINE_H}px` }}>{char}</span>
            <span style={{ display: "block", height: `${LINE_H}px` }}>{char}</span>
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function Sidebar() {
  const location = useLocation();
  const { open, close } = useSidebar();
  const { theme, toggleWithRay, resetTheme } = useTheme();
  const isLoggedIn = !!localStorage.getItem("token");
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  const [hasOpened, setHasOpened] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => { if (!isDesktop) close(); }, [location.pathname, isDesktop, close]);

  if (open && !hasOpened) setHasOpened(true);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/browse" || location.pathname === "/home";
    if (path === "/sellers") return location.pathname.startsWith("/sellers") || location.pathname.startsWith("/users");
    return location.pathname.startsWith(path);
  };

  const navItems = [
    ...(isDesktop ? [
      { to: "/browse", label: "Home" },
      { to: "/sellers", label: "Find Sellers" },
      { to: "/post", label: "Post Item" },
    ] : []),
    ...(isLoggedIn ? [
      { to: "/dashboard", label: "Dashboard" },
      ...(isDesktop ? [{ to: "/orders", label: "Orders" }] : []),
      { to: "/transactions", label: "Transactions" },
    ] : []),
  ];

  const mobileExtra = !isDesktop && isLoggedIn ? [
    { to: "/watching", label: "Watching" },
    { to: "/settings", label: "Settings" },
  ] : [];

  const logout = () => { resetTheme(); localStorage.removeItem("token"); localStorage.removeItem("user"); window.location.href = "/"; };

  const appPages = [
    "/",
    "/sellers",
    "/users",
    "/items",
    "/post",
    "/dashboard",
    "/orders",
    "/transactions",
    "/cart",
    "/messages",
    "/watching",
    "/settings",
    "/seller/setup",
  ];
  const hasActive = appPages.some((p) => isActive(p));
  const sidebarBg = hasActive || (!isDesktop && open)
    ? (theme === "dark" ? "#292524" : "#EDECEB")
    : "var(--bg)";

  return (
    <aside
      className={`fixed left-0 z-[80] w-[260px] flex flex-col ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      style={{ top: "64px", ...(isDesktop ? { bottom: 0 } : {}), transform: open ? "translateX(0)" : "translateX(-100%)", transition: "transform 200ms ease-out, opacity 200ms ease-out, background-color 300ms ease", backgroundColor: sidebarBg }}
    >
      <nav className="px-4 pt-4 pb-3 space-y-0 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: "none" }}>
        {navItems.map((item, i) => {
          const active = isActive(item.to);
          return (
            <motion.div
              key={item.to}
              initial={hasOpened ? false : { opacity: 0, y: 20 }}
              animate={open ? { opacity: 1, y: 0 } : hasOpened ? { opacity: 0, y: 20 } : { opacity: 0, y: 20 }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.4, delay: open ? i * 0.06 : 0 }}
            >
              <Link
                to={item.to}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                className={`block px-3 py-2 rounded-xl cursor-pointer transition-colors duration-150 ${active ? "text-amber-600" : "text-text-secondary hover:text-text"} ${isDesktop && i === 0 ? "mt-5" : ""}`}
                style={{ fontWeight: 550 }}
              >
                <span className="text-[21px]" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  <LetterPullUp text={item.label} isHovered={hoveredIdx === i} />
                </span>
              </Link>
            </motion.div>
          );
        })}

        {mobileExtra.map((item, i) => {
          const active = isActive(item.to);
          const idx = navItems.length + i;
          return (
            <motion.div
              key={item.to}
              initial={hasOpened ? false : { opacity: 0, y: 20 }}
              animate={open ? { opacity: 1, y: 0 } : hasOpened ? { opacity: 0, y: 20 } : { opacity: 0, y: 20 }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.4, delay: open ? idx * 0.06 : 0 }}
            >
              <Link
                to={item.to}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                className={`block px-3 py-2.5 rounded-xl cursor-pointer transition-colors duration-150 ${active ? "text-text" : "text-text-secondary hover:text-text"}`}
                style={{ fontWeight: 550 }}
              >
                <span className="text-[21px]" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  <LetterPullUp text={item.label} isHovered={hoveredIdx === idx} />
                </span>
              </Link>
            </motion.div>
          );
        })}

        {(!isDesktop || !isLoggedIn) && (
          <div className="mt-6 pt-5 border-t border-border">
            <motion.div
              initial={hasOpened ? false : { opacity: 0, y: 20 }}
              animate={open ? { opacity: 1, y: 0 } : hasOpened ? { opacity: 0, y: 20 } : { opacity: 0, y: 20 }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.4, delay: open ? (navItems.length + mobileExtra.length) * 0.06 : 0 }}
            >
              <button
                onClick={() => { toggleWithRay(); }}
                className="flex items-center justify-between w-full px-3 py-2.5 rounded-xl cursor-pointer transition-colors duration-150 text-text-secondary hover:text-text"
              >
                <span className="text-[21px]" style={{ fontFamily: "'Instrument Serif', serif", fontWeight: 550 }}>
                  {theme === "dark" ? "Light mode" : "Dark mode"}
                </span>
                <span className="relative shrink-0 w-[42px] h-[24px] rounded-full border border-zinc-300 dark:border-border transition-colors duration-200" style={{ backgroundColor: theme === "dark" ? "var(--accent)" : "var(--surface-raised)" }}>
                  <span className="absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full bg-white shadow transition-transform duration-200" style={{ transform: theme === "dark" ? "translateX(18px)" : "translateX(0)" }} />
                </span>
              </button>
            </motion.div>

            {isLoggedIn && (
              <motion.div
                initial={hasOpened ? false : { opacity: 0, y: 20 }}
                animate={open ? { opacity: 1, y: 0 } : hasOpened ? { opacity: 0, y: 20 } : { opacity: 0, y: 20 }}
                transition={{ type: "spring", bounce: 0.2, duration: 0.4, delay: open ? (navItems.length + mobileExtra.length + 1) * 0.06 : 0 }}
              >
                <button
                  onClick={() => { close(); logout(); }}
                  className="block w-full text-left px-3 py-2.5 rounded-xl cursor-pointer transition-colors duration-150 text-danger hover:text-danger"
                  style={{ fontWeight: 550 }}
                >
                  <span className="text-[21px]" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    Sign out
                  </span>
                </button>
              </motion.div>
            )}
          </div>
        )}
      </nav>
    </aside>
  );
}
